import { useMemo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useMediaQuery } from '@mantine/hooks';
import {
  Container,
  Text,
  Card,
  Divider,
  Title,
  useMantineTheme,
  Paper,
  Stack,
  Box,
  rem,
  Grid,
} from '@mantine/core';
import { IconUser, IconCalendar, IconId, IconBook, IconCreditCard, IconArrowRight } from '@tabler/icons-react';
import { GetUserData, GetProgramaDescripcion, CreateMPPreference } from '../actions/PaymentActions';
import UserInfoRow from '../components/UserInfoRow';
import PaymentCard from '../components/PaymentCard';

function getMercadoPagoCheckoutUrl(preferenceId) {
  return `https://www.mercadopago.com.ar/checkout/v1/redirect?pref_id=${encodeURIComponent(preferenceId)}`;
}

const PaymentProcess = () => {
  const theme = useMantineTheme();
  const location = useLocation();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery('(max-width: 48em)');

  const queryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      data: params.get('data'),
    };
  }, [location.search]);

  const { data: userData } = useSelector((state) => state.payment.userData);
  const { data: programaData } = useSelector((state) => state.payment.programa);
  const { loading: mpLoading, data: mpPreference, error: mpError } = useSelector(
    (state) => state.payment.mpPreference
  );
  const mpPrefId = typeof mpPreference === 'string' ? mpPreference : mpPreference?.id || '';

  // Cuando SIGUE devuelve un texto de error en lugar del código de barras (ej: "Solicitud sin
  // verificar o vencida."), el pago se bloquea y acá queda el motivo para mostrarlo.
  const [pagoBloqueado, setPagoBloqueado] = useState(null);

  const normalize = (s) => (s ? s.normalize('NFD').replace(/\p{Diacritic}/gu, '') : '');

  function formatearNumero(numero, division = 1, locale = 'es-AR', decimales = 2) {
    if (numero === null || numero === undefined) return null;
    let valor;
    if (typeof numero === 'string') {
      valor = parseFloat(numero.replace(',', '.'));
    } else {
      valor = parseFloat(numero);
    }
    if (isNaN(valor)) return '-';
    const valorDividido = valor / division;
    const formatter = new Intl.NumberFormat(locale, {
      minimumFractionDigits: decimales,
      maximumFractionDigits: decimales,
    });
    return formatter.format(valorDividido);
  }

  const openMercadoPagoCheckout = () => {
    if (!mpPrefId) return;
    const url = getMercadoPagoCheckoutUrl(mpPrefId);
    console.log('[Checkout MP] Abriendo checkout en nueva pestaña:', url);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    if (userData) {
      dispatch(GetProgramaDescripcion(userData.programa));
    }
  }, [userData, dispatch]);

  useEffect(() => {
    if (queryParams.data) {
      const cadenaCorregida = queryParams.data.replace(/ /g, '+');
      dispatch(GetUserData(cadenaCorregida));
    }
  }, [queryParams, dispatch]);

  // Mercado Pago: grado, posgrado, eventos y microcredenciales
  useEffect(() => {
    if (!userData) return;

    const programaTxt = typeof programaData === 'string' ? programaData : '';
    if (userData.programaTipo === 'm' && !programaTxt) return;

    (async () => {
      const isMicro = userData.programaTipo === 'm';

      let descripcion;
      if (isMicro) {
        descripcion = `Web-UAI-${normalize(userData.legajoTemporal)}-${normalize(programaTxt)}-${normalize(userData.apellido)},${normalize(userData.nombre)} - Pago matricula Microcredencial`;
      } else {
        const campusTxt = userData.campus || '';
        const anioTxt = userData.anio || '';
        const turnoTxt = userData.turno || '';
        descripcion = `Preinscripcion Web UAI - ${normalize(userData.legajoTemporal)} ${normalize(userData.apellido)}, ${normalize(userData.nombre)}  - matricula - ${normalize(campusTxt)} - ${normalize(programaTxt)} - ${normalize(anioTxt)} ${normalize(turnoTxt)}`;
      }

      const d = new Date(userData.fechaVencimineto);
      const ymd = `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
      let codigoBarra = null;
      try {
        const url = `${process.env.REACT_APP_API_BASE_URL}/checkout/codigoBarra?solicitudId=${encodeURIComponent(userData.legajoTemporal)}&fechaVencimientoYMD=${encodeURIComponent(ymd)}&precioMatricula=${encodeURIComponent(userData.valorMatricula)}&tipoId=000`;
        const r = await fetch(url, { method: 'GET' });
        if (r.ok) {
          const txt = await r.text();
          try {
            codigoBarra = JSON.parse(txt);
          } catch {
            codigoBarra = txt.replace(/^"|"$/g, '');
          }
        }
      } catch (e) {
        /* noop */
      }

      // fact_fCodigoBarra (SIGUE) devuelve un TEXTO de error en lugar del código cuando la
      // solicitud no está en condiciones de pagarse (ej: "Solicitud sin verificar o vencida.").
      // Un código de barras real es numérico: si la respuesta trae letras o espacios, es un
      // mensaje de error → NO se crea la preferencia de pago (el webhook jamás podría conciliar
      // un pago con esa referencia) y se muestra el motivo al usuario.
      if (typeof codigoBarra === 'string' && /[A-Za-zÀ-ÿ\s]/.test(codigoBarra.trim())) {
        console.error('[Checkout MP] Pago bloqueado — SIGUE devolvió:', codigoBarra);
        setPagoBloqueado(codigoBarra.trim());
        return;
      }
      setPagoBloqueado(null);

      const referencia = codigoBarra ? `UAI-${codigoBarra}` : `UAI-${userData.legajoTemporal}`;

      const payload = {
        ItemPago: {
          Titulo: descripcion,
          Descripcion: descripcion,
          Precio: `${userData.valorMatricula}`,
        },
        Entidad: 'UAI',
        ReferenciaExterna: referencia,
        // Este flujo es siempre matricula/microcredencial: 1x/2x/3x sin interes (financiado por UAI)
        // + 4x-12x con interes a cargo del alumno (ticket MP WCS-42226).
        TipoCuotas: 'matricula',
      };
      console.log('[Checkout MP] POST /mpuai/pago?entidadId=uai', payload);
      dispatch(CreateMPPreference(payload));
    })();
  }, [userData, programaData, dispatch]);

  const programType =
    userData && userData.programaTipo
      ? userData.programaTipo === 'm'
        ? ' microcredencial'
        : ' matrícula'
      : '';

  const paymentCardProps = userData
    ? {
        title: 'PAGO DE MATRÍCULA',
        description: `Aboná el valor total de tu${programType} con Mercado Pago`,
        amountLabel: `Valor${programType}`,
        amount: `$${formatearNumero(userData.valorMatricula)}`,
        amountDetails: '',
        amountColor: '#006eaa',
        centerContent: true,
        compactMobile: isMobile,
        buttonText: 'Pagar con Mercado Pago',
        buttonCaption: isMobile
          ? 'Pagá en 1 o más cuotas. Se abre Mercado Pago en una nueva pestaña.'
          : 'Pagá en 1 o más cuotas. Se abre Mercado Pago en una nueva pestaña.',
        buttonIcon: <IconArrowRight size={isMobile ? 18 : 20} />,
        onButtonClick: openMercadoPagoCheckout,
        // pagoBloqueado también deshabilita por si quedó una preferencia vieja persistida en el store.
        isDisabled: mpLoading || !mpPrefId || !!mpError || !!pagoBloqueado,
        isLoading: mpLoading,
      }
    : null;

  const userRows = userData ? (
    <>
      <UserInfoRow
        compact={isMobile}
        icon={IconUser}
        value={`${userData.nombre} ${userData.apellido}`}
        label="Nombre"
        iconColor="#f20a61"
      />
      {!isMobile && <Divider my="xs" color="gray.2" />}
      <UserInfoRow
        compact={isMobile}
        icon={IconId}
        value={userData.legajoTemporal}
        label="Legajo temporal"
        iconColor="#f20a61"
      />
      {!isMobile && <Divider my="xs" color="gray.2" />}
      <UserInfoRow
        compact={isMobile}
        icon={IconBook}
        value={programaData}
        label="Programa"
        iconColor="#f20a61"
        lineClamp={isMobile ? 2 : undefined}
      />
      {!isMobile && <Divider my="xs" color="gray.2" />}
      <UserInfoRow
        compact={isMobile}
        icon={IconCalendar}
        value={new Date(userData.fechaVencimineto).toLocaleDateString('es-AR', {
          day: '2-digit',
          month: isMobile ? 'short' : 'long',
          year: 'numeric',
        })}
        label="Vencimiento de Pago"
        iconColor="#f20a61"
      />
    </>
  ) : null;

  return (
    <Container
      size="sm"
      py={isMobile ? 0 : 'xl'}
      px={isMobile ? 'xs' : undefined}
      className={isMobile ? 'pay-confirm-page' : undefined}
    >
      <Box
        className={isMobile ? 'pay-confirm-shell' : undefined}
        sx={{
          position: 'relative',
          borderRadius: isMobile ? rem(12) : rem(24),
          overflow: isMobile ? 'hidden' : 'hidden',
          boxShadow: isMobile ? theme.shadows.sm : theme.shadows.xl,
          background: 'rgba(255,255,255,0.85)',
          backdropFilter: 'blur(8px)',
          ...(isMobile ? { height: '100%', display: 'flex', flexDirection: 'column' } : {}),
        }}
      >
        <Card
          shadow={isMobile ? 'sm' : 'xl'}
          radius="lg"
          p={isMobile ? 'sm' : 'xl'}
          withBorder
          style={{
            background: 'transparent',
            boxShadow: 'none',
            ...(isMobile
              ? { flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'hidden' }
              : {}),
          }}
        >
          <Box className={isMobile ? 'pay-confirm-header' : undefined}>
            <Title
              order={isMobile ? 4 : 2}
              align="center"
              mb={isMobile ? 2 : 'xs'}
              fw={500}
              style={{ letterSpacing: isMobile ? 0 : 1 }}
            >
              <IconCreditCard
                size={isMobile ? 22 : 32}
                style={{ verticalAlign: 'middle', marginRight: isMobile ? 6 : 8, color: '#f20a61' }}
              />
              Confirmá tus datos
            </Title>
            {!isMobile && (
              <Text align="center" c="dimmed" size="sm" mb="lg">
                Verificá la información antes de continuar con el pago y la facturación.
              </Text>
            )}

            <Divider
              my={isMobile ? 6 : 'md'}
              label="Datos del usuario"
              labelPosition="center"
              size={isMobile ? 'xs' : 'sm'}
            />

            {pagoBloqueado && (
              <Paper
                withBorder
                p="md"
                radius="md"
                mt="sm"
                mb="sm"
                style={{ background: '#FDECEA', borderColor: '#F5C2C7' }}
              >
                <Text align="center" c="#842029" fw={600} size="sm">
                  No es posible realizar el pago: {pagoBloqueado}
                </Text>
                <Text align="center" c="#842029" size="xs" mt={4}>
                  Verificá tu solicitud desde el mail que recibiste o comunicate con la universidad.
                </Text>
              </Paper>
            )}
          </Box>

          {userData && paymentCardProps && (
            <Stack
              spacing={isMobile ? 0 : 'xl'}
              w={isMobile ? '100%' : '90%'}
              m="0 auto"
              className={isMobile ? 'pay-confirm-body' : undefined}
              style={isMobile ? { flex: 1, minHeight: 0 } : undefined}
            >
              {isMobile ? (
                <>
                  <Paper className="pay-confirm-card pay-confirm-card--data" shadow="xs" radius="md" p="md" withBorder>
                    <Stack gap={10} justify="center" style={{ flex: 1 }}>
                      <UserInfoRow
                        compact
                        icon={IconUser}
                        value={`${userData.nombre} ${userData.apellido}`}
                        label="Nombre"
                        iconColor="#f20a61"
                        lineClamp={2}
                      />
                      <UserInfoRow
                        compact
                        icon={IconId}
                        value={userData.legajoTemporal}
                        label="Legajo"
                        iconColor="#f20a61"
                      />
                      <UserInfoRow
                        compact
                        icon={IconBook}
                        value={programaData}
                        label="Programa"
                        iconColor="#f20a61"
                        lineClamp={2}
                      />
                      <UserInfoRow
                        compact
                        icon={IconCalendar}
                        value={new Date(userData.fechaVencimineto).toLocaleDateString('es-AR', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                        label="Vencimiento"
                        iconColor="#f20a61"
                      />
                    </Stack>
                  </Paper>
                  <Paper className="pay-confirm-card pay-confirm-card--pay" shadow="xs" radius="md" p="md" withBorder>
                    <Box style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <PaymentCard embedded {...paymentCardProps} />
                    </Box>
                  </Paper>
                </>
              ) : (
                <>
                  <Paper
                    shadow="md"
                    radius="md"
                    p="xl"
                    withBorder
                    style={{ background: '#fff', width: '100%' }}
                  >
                    {userRows}
                  </Paper>
                  <Grid grow gutter="sm" justify="center" align="flex-start" style={{ alignItems: 'stretch' }}>
                    <Grid.Col span={{ base: 12, sm: 8, md: 8 }}>
                      <PaymentCard {...paymentCardProps} />
                    </Grid.Col>
                  </Grid>
                </>
              )}
            </Stack>
          )}
        </Card>
      </Box>
    </Container>
  );
};

export default PaymentProcess;
